import { SwapVert } from '@mui/icons-material';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Autocomplete, Avatar, Box, Button, Chip, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify } from 'react-admin';
import useDebounce from '../../../hooks/useDebounce';
import {
  ChatsByPhoneAndDate,
  CommunicationDirection,
  FindAllMessagesParams,
  MessageCategory,
  MessageHistoryLookupDirection,
  MessageLogsByDateAndLastOutboundResponse,
  MessagePartyType,
  MessageResolutionStatus,
  SimplifiedMemberCommunicationStatus,
  SimplifiedMessageLog,
  TrackingNumberWithAgents,
  TWILIO_ERROR_STATUSES,
  TwilioMessageStatus,
} from '../../../interfaces/appointments';
import { Pagination } from '../../../interfaces/common';
import messagesService from '../../../services/appointments/messages';
import coreService from '../../../services/core';
import { DEFAULT_PAGE_SIZE, INITIAL_PAGINATION_STATE } from '../../../util/constants';
import { formatDateStringToMMDDYYYYAndHour } from '../../../util/dateFormat';
import { getCapitalizedWord } from '../../../util/stringFormat';
import { CustomTable } from '../../common/CustomTable';
import ChatDrawer, { OpenStatus, SimplifiedMember } from './ChatDrawer/ChatDrawer';
import './messages-center.css';
import {
  mergeChatsByPhone,
  simplifiedDeliveryStatusColor,
  simplifiedResolutionStatus,
  simplifiedResolutionStatusColor,
} from './utils';

import type { ReactNode } from 'react';
import { ClinicFilter } from '../ClinicFilter';
import AgentsFilter from '../filters/AgentsFilter';

type Props = {
  breadcrumbs: ReactNode;
};

const INITIAL_COMM_STATUS: SimplifiedMemberCommunicationStatus = {
  emailOptedOut: false,
  smsOptedOut: false,
};

const INFINITE_SCROLL_BATCH_SIZE = 50;

export const MessagesCenter = ({ breadcrumbs }: Props) => {
  const [messages, setMessages] = useState<SimplifiedMessageLog[]>([]);
  const [page, setPage] = useState<number>(1);
  const [filters, setFilters] = useState<FindAllMessagesParams>({
    resolutionStatus: [MessageResolutionStatus.Pending],
  });
  const [pagination, setPagination] = useState<Pagination>(INITIAL_PAGINATION_STATE);
  const [selectedMessage, setSelectedMessage] = useState<SimplifiedMessageLog | null>(null);
  const [members, setMembers] = useState<SimplifiedMember[]>([]);
  const [canEditMember, setCanEditMember] = useState<boolean>(true);
  const [loadingChat, setLoadingChat] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [drawerStatus, setDrawerStatus] = useState<OpenStatus>({ source: 'none', open: false });
  const [messagesByNumberAndDate, setMessagesByNumberAndDate] = useState<ChatsByPhoneAndDate>({});
  const [memberCommunicationStatus, setMemberCommunicationStatus] =
    useState<SimplifiedMemberCommunicationStatus>(INITIAL_COMM_STATUS);
  const [resolutionDetails, setResolutionDetails] = useState<SimplifiedMessageLog | null>(null);
  const [deliveryDetails, setDeliveryDetails] = useState<SimplifiedMessageLog | null>(null);
  const [trackingNumbers, setTrackingNumbers] = useState<TrackingNumberWithAgents[]>([]);
  const [allMessages, setAllMessages] = useState<MessageLogsByDateAndLastOutboundResponse>({});

  const notify = useNotify();

  const fetchAllMessagesGroupedByDate = useCallback(async (memberId: number) => {
    const response = await messagesService.findAllMessagesGroupedByDate({
      coreMemberId: memberId,
      orderBy: 'asc',
    });
    return response.data || {};
  }, []);

  const fetchMessagesHistory = useCallback(
    async (phone: string, messageId?: number) => {
      if (!messageId) {
        const response = await messagesService.getMessageHistory({ phone });
        if (!response.data) {
          setLoadingChat(false);
          return;
        }
        return {
          memberCommunicationStatus: response.data.memberCommunicationStatus,
          chats: response.data.chats,
        };
      } else {
        const [messagesBackwardHistory, messagesForwardHistory] = await Promise.all([
          messagesService.getMessageHistory({
            phone,
            messageId,
            lookupDirection: MessageHistoryLookupDirection.Backward,
          }),
          messagesService.getMessageHistory({
            phone,
            messageId,
            lookupDirection: MessageHistoryLookupDirection.Forward,
          }),
        ]);

        if (!messagesBackwardHistory.data || !messagesForwardHistory.data) {
          setLoadingChat(false);
          return;
        }

        return {
          memberCommunicationStatus: {
            ...messagesBackwardHistory.data.memberCommunicationStatus,
            ...messagesForwardHistory.data.memberCommunicationStatus,
          },
          chats: mergeChatsByPhone(
            trackingNumbers,
            messagesBackwardHistory.data.chats,
            messagesForwardHistory.data.chats,
          ),
        };
      }
    },
    [trackingNumbers],
  );

  const loadOlderMessages = useCallback(
    async (phone: string, systemPhone: string, beforeMessageId: number) => {
      try {
        const response = await messagesService.getMessageHistory({
          phone,
          systemPhone,
          beforeMessageId,
          limit: INFINITE_SCROLL_BATCH_SIZE,
        });

        if (!response.data) {
          return null;
        }

        return response.data.chats;
      } catch (error) {
        console.error('Error loading older messages:', error);
        notify('Failed to load older messages', 'error');
        return null;
      }
    },
    [notify],
  );

  const loadNewerMessages = useCallback(
    async (phone: string, systemPhone: string, afterMessageId: number) => {
      try {
        const response = await messagesService.getMessageHistory({
          phone,
          systemPhone,
          afterMessageId,
          limit: INFINITE_SCROLL_BATCH_SIZE,
        });

        if (!response.data) {
          return null;
        }

        return response.data.chats;
      } catch (error) {
        console.error('Error loading newer messages:', error);
        notify('Failed to load newer messages', 'error');
        return null;
      }
    },
    [notify],
  );

  const fetchAndSetMemberChatHistory = useCallback(
    async ({
      phone,
      messageId,
      memberId,
    }: {
      phone: string;
      messageId?: number;
      memberId: number;
    }) => {
      setLoadingChat(true);
      const allMessagesGroupedByDate = await fetchAllMessagesGroupedByDate(memberId);
      const messagesHistory = await fetchMessagesHistory(phone, messageId);

      if (!messagesHistory) {
        notify('An error occurred while getting messages', 'error', { autoHideDuration: 15 });
        setLoadingChat(false);
        return;
      }

      setAllMessages(allMessagesGroupedByDate);
      setMessagesByNumberAndDate(messagesHistory.chats);
      setMemberCommunicationStatus(messagesHistory.memberCommunicationStatus);
      setLoadingChat(false);
    },
    [fetchAllMessagesGroupedByDate, fetchMessagesHistory, notify],
  );

  const handleRowClick = useCallback(
    async (message: SimplifiedMessageLog) => {
      setCanEditMember(false);
      setDrawerStatus({ source: 'row', open: true });

      const { from, to } = message;
      const target = from.fromType === MessagePartyType.USER ? from : to;

      await fetchAndSetMemberChatHistory({
        phone: target.phoneNumber,
        messageId: message.id,
        memberId: target.user?.coreMemberId!,
      });

      setSelectedMessage(message);
    },
    [fetchAndSetMemberChatHistory],
  );

  const fetchMessagesByFilters = useCallback(
    async (filtersParam?: FindAllMessagesParams) => {
      setLoading(true);
      const response = await messagesService.findAllMessages({
        ...(filtersParam &&
          Object.entries(filtersParam).reduce(
            (prev, [filter, value]) => ({ ...prev, ...(value && { [filter]: value }) }),
            {},
          )),
        page,
        pageSize: DEFAULT_PAGE_SIZE,
      });

      if (!response?.data || !response.pagination) {
        notify(response.message || 'There is a problem fetching messages');
        setLoading(false);
        return;
      }

      setPagination(response.pagination);
      setMessages(response.data);
      setLoading(false);
    },
    [notify, page],
  );

  const searchUsersByTerm = useCallback(
    async ({
      searchTerm,
      target,
    }: {
      searchTerm?: string | null;
      target: 'providers' | 'members';
    }) => {
      if (!searchTerm) {
        return [];
      }
      const { data } = await coreService.fetchMembers({ searchTerm, target });
      return data.map((member) => ({
        id: member.id,
        phone: member.phone,
        name: member.fullName,
      }));
    },
    [],
  );

  // Debounced handlers
  const debouncedFetchMessagesByFilters = useDebounce(fetchMessagesByFilters);

  const handleFilterChanges = useCallback(
    (filter?: {
      [filter: string]: boolean | number | string | undefined | string[] | number[];
    }) => {
      const updatedFilters = {
        ...filters,
        ...filter,
      };
      setFilters(updatedFilters);
      debouncedFetchMessagesByFilters(updatedFilters);
    },
    [filters, debouncedFetchMessagesByFilters],
  );

  const debouncedFetchMembers = useDebounce(
    useCallback(
      async (param: { searchTerm: string }) => {
        setMembers(await searchUsersByTerm({ ...param, target: 'members' }));
      },
      [searchUsersByTerm],
    ),
  );

  const handleRefresh = useCallback(() => {
    setPage(1);
    fetchMessagesByFilters(filters);
  }, [fetchMessagesByFilters, filters]);

  const handleExportMessages = useCallback(async () => {
    const response = await messagesService.exportMessages({
      ...filters,
      ...(filters.coreProviderId && { coreProviderId: Number(filters.coreProviderId) }),
      ...(filters.coreMemberId && { coreMemberId: Number(filters.coreMemberId) }),
    });

    notify(
      response.message || 'Requested export message will send to your email shortly',
      'success',
      { autoHideDuration: 15 },
    );
  }, [filters, notify]);

  // Fetch tracking numbers and initial messages
  useEffect(() => {
    let isMounted = true;
    (async () => {
      const trackingNumbersResp = await messagesService.getTrackingNumbers();
      if (isMounted) {
        setTrackingNumbers(trackingNumbersResp.data?.availableNumbers || []);
        await fetchMessagesByFilters(filters);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [fetchMessagesByFilters, filters, page]);

  // Memoized columns for CustomTable
  const columns = useMemo(
    () => [
      {
        key: 'action',
        label: <SwapVert />,
        width: '30px',
        minWidth: '30px',
      },
      { key: 'deliveryStatus', label: 'Delivery Status', width: '80px', minWidth: '80px' },
      { key: 'from', label: 'From', width: '100px', minWidth: '100px' },
      { key: 'to', label: 'To', width: '100px', minWidth: '100px' },
      { key: 'message', label: 'Message', width: '150px', minWidth: '150px' },
      { key: 'messageCategory', label: 'Category', width: '50px', minWidth: '100px' },
      { key: 'createdAt', label: 'Created At', width: '100px', minWidth: '100px' },
      { key: 'status', label: 'Solved by?', width: '80px', minWidth: '80px' },
    ],
    [],
  );

  // Memoized rows for CustomTable
  const rows = useMemo(
    () =>
      messages.map((data) => {
        const isInbound = data.direction === CommunicationDirection.INBOUND;
        const isFailure = TWILIO_ERROR_STATUSES.includes(
          data.delivery?.status as TwilioMessageStatus,
        );
        const resolution = simplifiedResolutionStatus(data.resolution?.status);
        const resolutionColor = simplifiedResolutionStatusColor(data.resolution?.status);

        const { from, to } = data;

        const fromPhoneBelongsToUser = from.fromType === MessagePartyType.USER;
        const toPhoneBelongToUser = to.toType === MessagePartyType.USER;

        return {
          onClick: () => handleRowClick(data),
          action: isInbound ? (
            <CallReceivedIcon sx={{ color: '#AF77BE' }} />
          ) : (
            <CallMadeIcon sx={{ color: '#389FBA' }} />
          ),
          from: (
            <Box flexDirection='column' display='flex'>
              {fromPhoneBelongsToUser
                ? from.user?.coreMemberFullname || 'Unknown'
                : from.provider?.coreProviderFullname || 'System'}
              {fromPhoneBelongsToUser && !!from.user?.coreMemberId && (
                <p style={{ margin: 0, color: '#8f8f8f' }}>ID: {from.user?.coreMemberId}</p>
              )}
              {!fromPhoneBelongsToUser && !!from.provider?.coreProviderId && (
                <p style={{ margin: 0, color: '#8f8f8f' }}>ID: {from.provider?.coreProviderId}</p>
              )}
              <p style={{ margin: '1px 0 1px', color: '#8f8f8f' }}>{data.from.phoneNumber}</p>
            </Box>
          ),
          to: (
            <Box flexDirection='column' display='flex'>
              {toPhoneBelongToUser
                ? to.user?.coreMemberFullname || 'Unknown'
                : to.provider?.coreProviderFullname || 'System'}
              {toPhoneBelongToUser && !!to.user?.coreMemberId && (
                <p style={{ margin: 0, color: '#8f8f8f' }}>ID: {to.user?.coreMemberId}</p>
              )}
              {!toPhoneBelongToUser && !!to.provider?.coreProviderId && (
                <p style={{ margin: 0, color: '#8f8f8f' }}>ID: {to.provider?.coreProviderId}</p>
              )}
              <p style={{ margin: '1px 0 1px', color: '#8f8f8f' }}>{data.to.phoneNumber}</p>
            </Box>
          ),
          message: (
            <p style={{ textAlign: 'justify' }} className='message-content'>
              {data.content}
            </p>
          ),
          messageCategory: (
            <Chip
              size='small'
              className='message-table-chip'
              label={data.messageCategory.split('-').map(getCapitalizedWord).join(' ')}
            />
          ),
          createdAt: formatDateStringToMMDDYYYYAndHour(data.createdAt),
          deliveryStatus: (
            <Box
              display='flex'
              flexDirection='column'
              alignItems='flex-start'
              justifyContent='flex-start'
              gap={1}
              onMouseEnter={() => (isFailure ? setDeliveryDetails(data) : undefined)}
              onMouseLeave={() => setDeliveryDetails(null)}>
              {deliveryDetails && deliveryDetails.id === data.id && (
                <Box
                  flexDirection='column'
                  display='flex'
                  alignItems='flex-start'
                  gap={1}
                  sx={{
                    position: 'absolute',
                    zIndex: 100,
                    backgroundColor: 'white',
                    translate: '-35px -75px',
                    padding: '15px',
                    borderRadius: '5px',
                    boxShadow: '0 0 5px rgba(0, 0, 0, 0.2)',
                  }}>
                  <Box flexDirection='column' display='flex' alignItems='flex-start' gap={0}>
                    <Typography fontWeight='bold'>Error code:</Typography>
                    <Chip
                      size='small'
                      label={deliveryDetails.delivery?.errorDetails?.code}
                      color='error'
                    />
                  </Box>
                  <Box flexDirection='column' display='flex' alignItems='flex-start' gap={0}>
                    <Typography fontWeight='bold'>ID:</Typography>
                    <Typography>{deliveryDetails.id}</Typography>
                  </Box>
                  {deliveryDetails.delivery?.errorDetails?.message && (
                    <Box flexDirection='column' display='flex' alignItems='flex-start' gap={0}>
                      <Typography fontWeight='bold'>Error message:</Typography>
                      <Typography>{deliveryDetails.delivery?.errorDetails?.message}</Typography>
                    </Box>
                  )}
                  {deliveryDetails.delivery?.errorDetails?.description && (
                    <Box flexDirection='column' display='flex' alignItems='flex-start' gap={0}>
                      <Typography fontWeight='bold'>Error description:</Typography>
                      <Typography>{deliveryDetails.delivery?.errorDetails?.description}</Typography>
                    </Box>
                  )}
                </Box>
              )}
              <Chip
                size='small'
                className='message-table-chip'
                label={
                  <Box display='flex' flexDirection='row' alignItems='center'>
                    <Typography sx={{ marginRight: '5px' }}>{data.delivery?.status}</Typography>
                    {isFailure && <InfoIcon sx={{ width: 14, height: 14, color: '#fff' }} />}
                  </Box>
                }
                color={simplifiedDeliveryStatusColor(data.delivery?.status)}
              />
            </Box>
          ),
          status: (
            <Box
              flexDirection='column'
              display='flex'
              justifyContent='flex-start'
              alignItems='flex-start'
              onMouseEnter={() => (resolution === 'agent' ? setResolutionDetails(data) : undefined)}
              onMouseLeave={() => setResolutionDetails(null)}>
              {resolutionDetails && resolutionDetails.id === data.id && (
                <Box
                  flexDirection='column'
                  display='flex'
                  alignItems='flex-start'
                  gap={1}
                  sx={{
                    position: 'absolute',
                    zIndex: 100,
                    backgroundColor: 'white',
                    translate: '-35px -75px',
                    padding: '15px',
                    borderRadius: '5px',
                    boxShadow: '0 0 5px rgba(0, 0, 0, 0.2)',
                  }}>
                  <Box flexDirection='column' display='flex' alignItems='flex-start' gap={0}>
                    <Typography fontWeight='bold'>Resolution status:</Typography>
                    <Chip
                      size='small'
                      label={resolutionDetails.resolution?.status}
                      color={resolutionColor}
                    />
                  </Box>
                  {resolutionDetails.resolution?.resolvedAt && (
                    <Box flexDirection='column' display='flex' alignItems='flex-start' gap={0}>
                      <Typography fontWeight='bold'>Resolution date:</Typography>
                      <Typography>
                        {formatDateStringToMMDDYYYYAndHour(
                          resolutionDetails.resolution?.resolvedAt,
                        )}
                      </Typography>
                    </Box>
                  )}
                  <Box flexDirection='column' display='flex' alignItems='flex-start' gap={0}>
                    <Typography fontWeight='bold'>Resolved by:</Typography>
                    <Typography>
                      {resolutionDetails.resolution?.resolvedByCoreProviderFullname} (
                      {resolutionDetails.resolution?.resolvedByCoreProviderId})
                    </Typography>
                  </Box>
                </Box>
              )}
              <Chip
                size='small'
                color={simplifiedResolutionStatusColor(data.resolution?.status)}
                label={
                  <Box display='flex' flexDirection='row' alignItems='center'>
                    <Typography sx={{ marginRight: '5px' }}>{resolution}</Typography>
                    {resolution === 'agent' && (
                      <InfoIcon sx={{ width: 14, height: 14, color: '#fff' }} />
                    )}
                  </Box>
                }
                sx={{
                  maxWidth: '80%',
                  marginTop: 'auto',
                  marginBottom: 'auto',
                  fontSize: '12px',
                }}
              />
            </Box>
          ),
        };
      }),
    [messages, handleRowClick, deliveryDetails, resolutionDetails],
  );

  // Render
  return (
    <Box className='tab-section messages-center-section' marginTop={0}>
      <Box className='messages-center-header'>
        <Box display='flex' justifyContent='space-between' alignContent='center' marginBottom='8px'>
          {breadcrumbs}
          <Box display='flex' alignItems='flex-start' gap={1} flexWrap='wrap'>
            <Button
              className='messages-center-header-button'
              variant='contained'
              color='primary'
              onClick={handleExportMessages}>
              Export Messages
            </Button>
            <Button
              className='messages-center-header-button'
              size='small'
              variant='contained'
              onClick={() => {
                setDrawerStatus({ source: 'new', open: true });
                setSelectedMessage(null);
              }}>
              New message
            </Button>
          </Box>
        </Box>

        <Box display='flex' alignItems='flex-start' gap={1} mt={2} mb={3} flexWrap='wrap'>
          {/* Member Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start'>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>Member</Typography>
            <Autocomplete
              fullWidth
              className='member-selector'
              options={members}
              filterOptions={(x) => x}
              getOptionLabel={(option) => `${option.name} ID: ${option.id}`}
              onChange={(_, v) => handleFilterChanges({ coreMemberId: v?.id || '' })}
              noOptionsText='No members found'
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              value={members.find((m) => m.id === filters.coreMemberId) || undefined}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant='outlined'
                  placeholder='Enter a member name'
                  onChange={(e) => debouncedFetchMembers({ searchTerm: e.target.value })}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Avatar sx={{ color: 'action.active', mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component='li' {...props}>
                  <Avatar sx={{ mr: 2, bgcolor: '#007bff' }}>
                    {`${option.name?.[0] ?? ''}${option.name?.[1] ?? ''}`.toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography fontSize={'0.8rem'}>{option.name}</Typography>
                    <Typography variant='caption' color='textSecondary'>
                      ID: {option.id}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Box>

          {/* Agent Filter */}
          <AgentsFilter
            value={filters.coreProviderId}
            filterType='coreProviderId'
            handleFilterChanges={handleFilterChanges}
          />

          {/* From Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start'>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>From</Typography>
            <TextField
              type='number'
              value={filters.from || ''}
              key='autocomplete-from'
              size='small'
              placeholder='15555555555'
              sx={{ width: '180px' }}
              onChange={(e) => handleFilterChanges({ from: e.target.value })}
            />
          </Box>

          {/* To Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start'>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>To</Typography>
            <TextField
              type='number'
              value={filters.to || ''}
              key='autocomplete-to'
              size='small'
              placeholder='15555555555'
              sx={{ width: '180px' }}
              onChange={(e) => handleFilterChanges({ to: e.target.value })}
            />
          </Box>

          {/* Direction Filter */}
          <Box
            display='flex'
            flexDirection='column'
            alignItems='flex-start'
            className='messages-center-select'>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>Direction</Typography>
            <Autocomplete
              key='autocomplete-direction'
              size='small'
              options={Object.values(CommunicationDirection)}
              getOptionLabel={(option) =>
                `${option[0].toUpperCase()}${option.slice(1).toLowerCase()}`
              }
              onChange={(_, value) => handleFilterChanges({ direction: value ?? '' })}
              renderInput={(params) => (
                <TextField {...params} placeholder='Enter a direction' sx={{ width: '150px' }} />
              )}
              sx={{ width: '150px' }}
            />
          </Box>

          {/* Date, Category, Content, Delivery, Resolution, Refresh */}
          <Box display='flex' gap={1}>
            {/* Created At */}
            <Box display='flex' flexDirection='column' alignItems='flex-start'>
              <Typography>Created At</Typography>
              <Box display='flex' alignItems='flex-start' gap={2}>
                <TextField
                  size='small'
                  type='date'
                  label='Start Date'
                  value={filters.startDate || ''}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => handleFilterChanges({ startDate: e.target.value })}
                />
                <TextField
                  size='small'
                  type='date'
                  label='End Date'
                  value={filters.endDate || ''}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => handleFilterChanges({ endDate: e.target.value })}
                />
              </Box>
            </Box>

            {/* Message Category */}
            <Box
              display='flex'
              flexDirection='column'
              alignItems='flex-start'
              className='messages-center-select'>
              <Typography>Message Category</Typography>
              <Autocomplete
                fullWidth
                key='autocomplete-message-category'
                size='small'
                multiple
                options={Object.values(MessageCategory)}
                getOptionLabel={(option) =>
                  option
                    .split('-')
                    .map((w) => `${w[0].toUpperCase()}${w.slice(1)}`)
                    .join(' ')
                }
                value={filters.messageCategories || []}
                onChange={(_, value) =>
                  handleFilterChanges({
                    messageCategories: value,
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Enter a category'
                    fullWidth
                    sx={{ minWidth: '150px' }}
                  />
                )}
              />
            </Box>

            {/* Message Content */}
            <Box display='flex' flexDirection='column' alignItems='flex-start'>
              <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>
                Message Content
              </Typography>
              <TextField
                type='text'
                value={filters.searchString || ''}
                key='autocomplete-search-string'
                size='small'
                placeholder='...'
                sx={{ width: '206px' }}
                onChange={(e) => handleFilterChanges({ searchString: e.target.value })}
              />
            </Box>

            {/* Delivery Status */}
            <Box
              display='flex'
              flexDirection='column'
              alignItems='flex-start'
              className='messages-center-select'>
              <Typography>Delivery Status</Typography>
              <Autocomplete
                fullWidth
                key='autocomplete-delivery-status'
                size='small'
                multiple
                options={Object.values(TwilioMessageStatus)}
                getOptionLabel={(option) =>
                  option
                    .split('-')
                    .map((w) => `${w[0].toUpperCase()}${w.slice(1)}`)
                    .join(' ')
                }
                value={filters.deliveryStatus || []}
                onChange={(_, value) =>
                  handleFilterChanges({
                    deliveryStatus: value,
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Enter a delivery status'
                    fullWidth
                    sx={{ minWidth: '180px' }}
                  />
                )}
              />
            </Box>

            {/* Resolution Status */}
            <Box
              display='flex'
              flexDirection='column'
              alignItems='flex-start'
              className='messages-center-select'>
              <Typography>Resolution Status</Typography>
              <Autocomplete
                fullWidth
                key='autocomplete-resolution-status'
                size='small'
                multiple
                options={Object.values(MessageResolutionStatus)}
                getOptionLabel={(option) =>
                  option
                    .split('_')
                    .map((w) => `${w[0].toUpperCase()}${w.slice(1).toLowerCase()}`)
                    .join(' ')
                }
                value={filters.resolutionStatus || []}
                onChange={(_, value) =>
                  handleFilterChanges({
                    resolutionStatus: value,
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Enter a resolution status'
                    fullWidth
                    sx={{ minWidth: '180px' }}
                  />
                )}
              />
            </Box>

            {/* Clinic Filter */}
            <ClinicFilter
              selectedClinicIds={filters.clinicIds || []}
              includeUnassigned={!!filters.includeUnassigned}
              onChange={({ clinicIds, includeUnassigned }) =>
                handleFilterChanges({ clinicIds, includeUnassigned })
              }
            />

            {/* Refresh */}
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
              className='messages-center-select'>
              <Typography>&nbsp;</Typography>
              <Box display='flex' alignItems='center' gap={2}>
                <RefreshIcon onClick={handleRefresh} sx={{ cursor: 'pointer' }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <CustomTable
        columns={columns}
        defaultText='No registered messages'
        key='communications-center-table'
        loading={loading}
        rows={rows}
        pagination={{
          rowsPerPageOptions: [DEFAULT_PAGE_SIZE],
          rowPerPage: pagination.perPage,
          page: pagination.currentPage,
          totalCount: pagination.totalCount,
          onPageChange: (page: number) => setPage(page),
        }}
      />

      <ChatDrawer
        drawerStatus={drawerStatus}
        trackingNumbers={trackingNumbers}
        canEditMember={canEditMember}
        loading={loadingChat}
        allMessages={allMessages}
        messages={messagesByNumberAndDate}
        memberCommunicationStatus={memberCommunicationStatus}
        selectedMessage={selectedMessage || null}
        onLoadOlderMessages={loadOlderMessages}
        onLoadNewerMessages={loadNewerMessages}
        onSelectMember={useCallback(
          (data) => {
            if (!data) {
              setAllMessages({});
              setMessagesByNumberAndDate({});
              setCanEditMember(true);
              return;
            }
            fetchAndSetMemberChatHistory(data);
          },
          [fetchAndSetMemberChatHistory],
        )}
        onClose={useCallback(() => {
          setAllMessages({});
          setMessagesByNumberAndDate({});
          setCanEditMember(true);
          setDrawerStatus({ source: 'none', open: false });
        }, [])}
      />
    </Box>
  );
};
